// src/components/RPA/buttons/FormulateTestingAttributesButton.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const POLL_MS = 5000;        // Poll every 5 seconds
const MAX_MINUTES = 15;      // Stop polling after 15 minutes

const FormulateTestingAttributesButton = ({ docId, disabledHint, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // analysis result and/or status
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // progress & status
  const [phase, setPhase] = useState('idle');      // idle | queued | formulating | done | error
  const [queuedMsg, setQueuedMsg] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const timerRef = useRef(null);
  const tickRef = useRef(null);

  // --- helpers --------------------------------------------------------------

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const closePanel = () => {
    stopTimers();
    setOpen(false);
  };

  // Poll /admin/rpa/docs for this docId, return doc or null
  const fetchDocMeta = async () => {
    const res = await axios.get(`${api}/admin/rpa/docs`, { withCredentials: true });
    const docs = Array.isArray(res?.data?.items) ? res.data.items : [];
    return docs.find(d => String(d._id) === String(docId)) || null;
  };

  // Polling loop when queued
  const beginPolling = () => {
    // elapsed timer
    tickRef.current = setInterval(() => {
      setElapsedSec(sec => sec + 1);
    }, 1000);

    // status poller
    timerRef.current = setInterval(async () => {
      try {
        const d = await fetchDocMeta();
        if (!d) return;

        // If testingAttributes present, we are done
        if (d.testingAttributes) {
          stopTimers();
          setPhase('done');
          setResult({
            success: true,
            analysis: { 
              azure: true, 
              attributes: d.testingAttributes 
            },
          });
          if (typeof onDone === 'function') onDone(); // refresh doc list
          return;
        }

        // Still queued/working — update the UI hints
        if (d.testingAttributesQueuedAt) {
          setPhase('formulating');
          setLastUpdatedAt(d.updatedAt || d.createdAt || null);
        }

        // Safety stop after MAX_MINUTES
        if (elapsedSec >= MAX_MINUTES * 60) {
          stopTimers();
          setPhase('error');
          setErr('Testing attributes formulation timed out. Please try again or check the control objectives.');
        }
      } catch (e) {
        // transient errors — keep polling unless it persists too long
        console.warn('[RPA] testing attributes status poll failed:', e?.message || e);
      }
    }, POLL_MS);
  };

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setResult(null);
    setElapsedSec(0);
    setLastUpdatedAt(null);
    stopTimers();

    try {
      // kick off testing attributes formulation
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/formulate/attributes`,
        {},
        { withCredentials: true, validateStatus: () => true }
      );

      // 200 => formulation is already available (cached) or completed inline
      if (res.status === 200) {
        setResult(res.data || { message: 'No data returned' });
        setPhase('done');
        setOpen(true);
        if (typeof onDone === 'function') onDone(); // refresh doc list
        return;
      }

      // 202 => queued; start polling
      if (res.status === 202) {
        setPhase('queued');
        setQueuedMsg(res.data?.message || 'Queued for testing attributes formulation...');
        setOpen(true);
        beginPolling();
        return;
      }

      // other => error
      console.error('[RPA] formulate testing attributes unexpected:', res.status, res.data);
      setErr(res.data?.message || 'Testing attributes formulation failed.');
      setPhase('error');
      setOpen(true);
    } catch (e) {
      console.error('[RPA] formulate testing attributes failed:', e);
      setErr('Testing attributes formulation failed.');
      setPhase('error');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Clean up timers if component unmounts or panel closes
  useEffect(() => {
    return () => stopTimers();
  }, []);

  // --- render helpers -------------------------------------------------------

  const renderSourceLabel = () => {
    if (!result) return null;

    // If Azure AI responded
    if (result.analysis?.azure) {
      return (
        <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
          ✅ AI-Generated Testing Attributes
        </p>
      );
    }

    // Otherwise fallback heuristic
    return (
      <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
        ⚠️ Heuristic Formulation (Fallback)
      </p>
    );
  };

  const renderTestingAttributes = () => {
    if (!result?.analysis?.attributes) return null;

    let attributes;
    try {
      attributes = typeof result.analysis.attributes === 'string' 
        ? JSON.parse(result.analysis.attributes) 
        : result.analysis.attributes;
    } catch {
      return <p style={{ color: '#666' }}>Unable to parse testing attributes</p>;
    }

    const testingAttrs = attributes.testing_attributes || [];
    const strategy = attributes.testing_strategy || {};

    return (
      <div style={attributesContainer}>
        {/* Strategy Summary */}
        <div style={strategyBox}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            Testing Strategy
          </h4>
          <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <div><strong>Total Attributes:</strong> {strategy.total_attributes || testingAttrs.length}</div>
            <div><strong>Automation Coverage:</strong> {strategy.automation_coverage || 'Not specified'}</div>
            <div><strong>Testing Complexity:</strong> {strategy.testing_complexity || 'Not specified'}</div>
            <div><strong>Estimated Time:</strong> {strategy.estimated_testing_time || 'Not estimated'} hours per 100 items</div>
            <div><strong>Recommended Sample Size:</strong> {strategy.recommended_sample_size || 'Not specified'}</div>
          </div>
        </div>

        {/* Testing Attributes List */}
        {testingAttrs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Testing Attributes ({testingAttrs.length})
            </h4>
            <div style={attributesGrid}>
              {testingAttrs.slice(0, 6).map((attr, idx) => (
                <div key={idx} style={attributeCard}>
                  <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                    {attr.attribute_id || `Attribute ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#374151', marginBottom: 4 }}>
                    {attr.attribute_name}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                    <strong>Procedure:</strong> {attr.testing_procedure}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                    <strong>Data Required:</strong> {attr.data_requirements}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
                    <strong>Acceptance:</strong> {attr.acceptance_criteria}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ ...badge, background: getAutomationColor(attr.automation_level) }}>
                      {attr.automation_level || 'manual'}
                    </span>
                    <span style={{ ...badge, background: '#fef3c7', color: '#92400e' }}>
                      {attr.risk_focus || 'general'}
                    </span>
                    <span style={{ ...badge, background: '#f0f9ff', color: '#0369a1' }}>
                      {attr.sampling_method || 'statistical'}
                    </span>
                  </div>
                </div>
              ))}
              {testingAttrs.length > 6 && (
                <div style={{ ...attributeCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    +{testingAttrs.length - 6} more attributes...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Requirements Summary */}
        {strategy.data_requirements_summary && (
          <div style={{ marginTop: 16, padding: 10, background: '#f0fdf4', borderRadius: 6, border: '1px solid #22c55e' }}>
            <strong style={{ fontSize: 12, color: '#15803d' }}>Data Requirements Summary:</strong>
            <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
              {strategy.data_requirements_summary}
            </div>
          </div>
        )}

        {testingAttrs.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: 12 }}>
            No testing attributes were formulated. Please verify the control objectives contain testable criteria.
          </p>
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (phase === 'queued') {
      return (
        <div style={statusBox}>
          <strong>Queued for Formulation</strong>
          <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Processing control objectives... {formatElapsed(elapsedSec)}
            </span>
          </div>
        </div>
      );
    }

    if (phase === 'formulating') {
      return (
        <div style={statusBox}>
          <strong>Formulating Testing Attributes...</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            Converting control objectives into specific, executable testing procedures
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              This typically takes 2-4 minutes. Elapsed {formatElapsed(elapsedSec)}
              {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <ProgressBar />
        </div>
      );
    }

    if (phase === 'error') {
      return <p style={{ color: 'crimson' }}>{err || 'Something went wrong.'}</p>;
    }

    return null;
  };

  return (
    <div>
      <button
        onClick={run}
        disabled={busy || !!disabledHint}
        title={disabledHint || 'Formulate specific testing attributes from control objectives'}
        style={{ 
          background: '#8b5cf6', 
          color: 'white', 
          border: 'none', 
          padding: '4px 8px', 
          borderRadius: 4,
          fontSize: 12,
          cursor: disabledHint ? 'not-allowed' : 'pointer',
          opacity: disabledHint ? 0.6 : 1
        }}
      >
        {busy ? 'Formulating…' : 'Formulate Tests'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Testing Attributes Formulation</strong>
            <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
          </div>

          {renderStatus()}

          {/* Show the final result once ready */}
          {phase === 'done' && !err && result && (
            <>
              {renderSourceLabel()}
              {renderTestingAttributes()}
              
              {/* Raw JSON for debugging */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                  Raw Attributes (Debug)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 10, background: '#f8f9fa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                  {typeof result.analysis.attributes === 'string' 
                    ? result.analysis.attributes 
                    : JSON.stringify(result.analysis.attributes, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- helpers & styles ---------------------------- */

function getAutomationColor(level) {
  switch (level) {
    case 'fully_automated': return '#ecfdf5';
    case 'semi_automated': return '#fef3c7';
    case 'manual': return '#fef2f2';
    default: return '#f3f4f6';
  }
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  maxWidth: 900,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const statusBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 10,
  borderRadius: 8,
  marginBottom: 10
};

const attributesContainer = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8,
  marginBottom: 8
};

const strategyBox = {
  background: '#f0f9ff',
  border: '1px solid #3b82f6',
  padding: 10,
  borderRadius: 6
};

const attributesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 8
};

const attributeCard = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  padding: 10,
  borderRadius: 6,
  minHeight: 160
};

const badge = {
  fontSize: 9,
  padding: '2px 6px',
  borderRadius: 12,
  fontWeight: 'bold',
  textTransform: 'uppercase'
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      border: '2px solid #cbd3e1',
      borderTop: '2px solid #8b5cf6',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    }}
  />
);

const ProgressBar = () => (
  <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
    <div style={{
      width: '60%',
      height: '100%',
      background: '#8b5cf6',
      animation: 'bar 2s ease-in-out infinite'
    }} />
    <style>{`
      @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
      @keyframes bar {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(50%); }
        100% { transform: translateX(150%); }
      }
    `}</style>
  </div>
);

export default FormulateTestingAttributesButton;